/**
 * useNotifications Hook
 *
 * Manages notification campaign state: segments loading, form data, and API calls.
 */

import { useState, useEffect, useCallback } from "react";
import { notificationsApi } from "../api";
import type { SegmentSummary, NotificationFormData } from "../types";

const INITIAL_FORM: NotificationFormData = {
  segmentId: "",
  title: "",
  body: "",
  link: "",
  imageUrl: "",
  actionButtons: [],
};

export function useNotifications(brandId: string) {
  // Segments state
  const [segments, setSegments] = useState<SegmentSummary[]>([]);
  const [segmentsLoading, setSegmentsLoading] = useState(true);
  const [segmentsError, setSegmentsError] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState<NotificationFormData>(INITIAL_FORM);

  // UI state
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Load segments — reusable para retry (Audit C-08)
  const loadSegments = useCallback(async () => {
    setSegmentsLoading(true);
    setSegmentsError(null);
    try {
      const res = await notificationsApi.getSegments(brandId);
      if (res.success) {
        setSegments(res.data);
      } else {
        setSegmentsError(res.error || "No pudimos cargar tus segmentos.");
      }
    } catch (err) {
      console.error("Error loading segments:", err);
      setSegmentsError("No pudimos cargar tus segmentos. Revisá tu conexión.");
    } finally {
      setSegmentsLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    loadSegments();
  }, [loadSegments]);

  // Selected segment details
  const selectedSegment = segments.find((s) => s.id === form.segmentId) || null;

  // Form validation — push requiere segmento + título + cuerpo. Sin cuerpo
  // la notif llega con la línea inferior vacía y la UX en device es pobre.
  const isValid =
    !!form.segmentId &&
    form.title.trim().length > 0 &&
    form.body.trim().length > 0;

  // Update form fields
  const updateForm = useCallback((updates: Partial<NotificationFormData>) => {
    setForm((prev) => ({ ...prev, ...updates }));
    setError(null);
    setSuccess(false);
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setForm(INITIAL_FORM);
    setImagePreview(null);
    setError(null);
    setSuccess(false);
  }, []);

  // Send notification
  const sendNotification = useCallback(async () => {
    if (!isValid) return false;

    setSending(true);
    setError(null);
    try {
      // Step 1: Create campaign
      const createRes = await notificationsApi.create({
        brandId,
        segmentId: form.segmentId,
        title: form.title.trim(),
        body: form.body.trim(),
        link: form.link.trim() || undefined,
        imageUrl: form.imageUrl || undefined,
      });

      if (!createRes.success) {
        setError(createRes.error || "Error al crear la campaña");
        return false;
      }

      // Step 2: Send immediately
      const sendRes = await notificationsApi.send(createRes.data.id);
      if (!sendRes.success) {
        setError(sendRes.error || "Error al enviar");
        return false;
      }

      setSuccess(true);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Error inesperado");
      return false;
    } finally {
      setSending(false);
    }
  }, [brandId, form, isValid]);

  // Schedule notification
  const scheduleNotification = useCallback(
    async (scheduledAt: string) => {
      if (!isValid) return false;

      setSending(true);
      setError(null);
      try {
        const createRes = await notificationsApi.create({
          brandId,
          segmentId: form.segmentId,
          title: form.title.trim(),
          body: form.body.trim(),
          link: form.link.trim() || undefined,
          imageUrl: form.imageUrl || undefined,
        });

        if (!createRes.success) {
          setError(createRes.error || "Error al crear");
          return false;
        }

        const scheduleRes = await notificationsApi.schedule(
          createRes.data.id,
          scheduledAt,
        );
        if (!scheduleRes.success) {
          setError(scheduleRes.error || "Error al programar");
          return false;
        }

        setSuccess(true);
        return true;
      } catch (err: any) {
        setError(
          err?.response?.data?.error || err.message || "Error inesperado",
        );
        return false;
      } finally {
        setSending(false);
      }
    },
    [brandId, form, isValid],
  );

  return {
    // Segments
    segments,
    segmentsLoading,
    segmentsError,
    reloadSegments: loadSegments,
    selectedSegment,

    // Form
    form,
    updateForm,
    resetForm,
    isValid,
    imagePreview,
    setImagePreview,

    // Actions
    sendNotification,
    scheduleNotification,
    sending,
    error,
    success,
  };
}
